import { CSSProperties, useCallback, useState } from 'react'
import { Minus, Plus } from 'lucide-react'

export interface NumberFieldProps {
    label?: string
    description?: string
    error?: string
    value?: number
    defaultValue?: number
    onChange?: (value: number) => void
    min?: number
    max?: number
    step?: number
    disabled?: boolean
    required?: boolean
    variant?: 'primary' | 'secondary'
    style?: CSSProperties
}

export const NumberField = ({
    label,
    description,
    error,
    value: controlledValue,
    defaultValue = 1,
    onChange,
    min,
    max,
    step = 1,
    disabled = false,
    required = false,
    variant = 'primary',
    style,
}: NumberFieldProps) => {
    const [internalValue, setInternalValue] = useState(defaultValue)
    const val = controlledValue !== undefined ? controlledValue : internalValue
    const hasError = !!error
    const isPrimary = variant === 'primary'

    const update = useCallback(
        (next: number) => {
            if (disabled) return
            if (min !== undefined && next < min) next = min
            if (max !== undefined && next > max) next = max
            if (controlledValue === undefined) setInternalValue(next)
            onChange?.(next)
        },
        [disabled, min, max, controlledValue, onChange]
    )

    const stepBtnClass = 'flex h-full w-8 shrink-0 cursor-pointer items-center justify-center p-0 text-foreground-muted disabled:cursor-not-allowed'

    return (
        <div className={`flex flex-col gap-1 ${disabled ? 'opacity-50' : ''}`} style={style}>
            {label && (
                <label className={`text-sm font-medium ${hasError ? 'text-danger' : 'text-foreground'}`}>
                    {label}
                    {required && <span className="ml-0.5 text-danger">*</span>}
                </label>
            )}

            <div
                className={`flex h-9 items-center overflow-hidden rounded-xl transition-shadow duration-150 ${isPrimary ? 'bg-surface shadow-field' : 'bg-default'} ${
                    hasError ? 'ring-2 ring-danger' : 'focus-within:ring-2 focus-within:ring-accent'
                }`}
            >
                <button type="button" onClick={() => update(val - step)} disabled={disabled} className={stepBtnClass}>
                    <Minus size={16} strokeWidth={1.5} />
                </button>
                <input
                    type="number"
                    value={val}
                    onChange={(e) => update(Number(e.target.value) || 0)}
                    disabled={disabled}
                    min={min}
                    max={max}
                    step={step}
                    className="h-full min-w-0 flex-1 bg-transparent p-0 text-center text-sm font-normal leading-5 text-foreground outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <button type="button" onClick={() => update(val + step)} disabled={disabled} className={stepBtnClass}>
                    <Plus size={16} strokeWidth={1.5} />
                </button>
            </div>

            {description && !error && <span className="text-sm font-normal text-foreground-muted">{description}</span>}
            {error && <span className="text-sm font-normal text-danger">{error}</span>}
        </div>
    )
}
